import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  IconButton,
  Typography,
  Paper,
  InputBase,
  useTheme,
  Grid,
  Divider,
  CircularProgress,
  Button,
} from "@mui/material";
import GridViewIcon from "@mui/icons-material/GridView";
import ListIcon from "@mui/icons-material/List";
import SearchIcon from "@mui/icons-material/Search";
import MenuIcon from "@mui/icons-material/Menu";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import GridView from "./Components/GridView/GridView";
import ListView from "./Components/ListView/ListView";
import CustomHeaderButton from "./Components/CustomHeaderButton/CustomHeaderButton";
import { Autocomplete, DatePicker, ValidatorForm } from "components";
import {
  fetchTopics,
  searchTopics,
  applyCategoryFilter,
  applyDateFilter,
  resetFiltersAndFetch,
  setFilters,
} from "../../redux/actions/topicsActions";
import CategoryServices from "services/CategoryServices";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";

const AllTopics = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isLight = theme.palette.mode === "light";
  const dispatch = useDispatch();

  // Redux state
  const { filters, loading } = useSelector((state) => state.topics);

  // Local component state
  const [view, setView] = useState("grid");
  const [filterView, setFilterView] = useState(false);
  const [searchValue, setSearchValue] = useState(filters.search || "");
  const [categoryData, setCategoryData] = useState([]);

  // Load topics on component mount
  useEffect(() => {
    dispatch(fetchTopics());
  }, [dispatch]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchValue.length >= 4) {
        dispatch(searchTopics(searchValue));
      } else if (searchValue.length === 0) {
        // If search is cleared, fetch all topics
        dispatch(searchTopics(""));
      }
    }, 500); // 500ms delay for debouncing

    return () => clearTimeout(timeoutId);
  }, [searchValue, dispatch]);

  // Handle manual search (for search button and Enter key)
  const handleSearch = () => {
    if (searchValue.length >= 4 || searchValue.length === 0) {
      dispatch(searchTopics(searchValue));
    }
  };

  // Handle category filter change
  const handleCategoryChange = (event, newValue) => {
    dispatch(applyCategoryFilter(newValue?.id || null));
  };

  // Handle date filter change
  const handleDateFilterChange = (fromDate, toDate) => {
    dispatch(applyDateFilter(fromDate, toDate));
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchValue("");
    dispatch(resetFiltersAndFetch());
  };

  const getCategoryData = useCallback(async () => {
    const response = await CategoryServices.get({
      status: "ACTIVE",
      limit: 1000,
    });

    if (response.success) {
      setCategoryData(response?.data?.view?.data || []);
    }
  }, []);

  useEffect(() => {
    getCategoryData();
  }, [getCategoryData]);

  return (
    <>
      {/* Header Bar for topic */}
      <Box
        p={1}
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderRadius: "14px",
          p: 2,
        }}
      >
        <Grid container spacing={2} alignItems="center">
          {/* Title */}
          <Grid
            item
            size={{ xs: 12, md: 3 }}
            display="flex"
            justifyContent={{ xs: "center", md: "flex-start" }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 600,
                fontSize: "1.5em",
              }}
            >
              {t("topics.allTopics")}
            </Typography>
          </Grid>

          {/* Search Bar */}
          <Grid
            item
            size={{ xs: 12, md: 6 }}
            display="flex"
            justifyContent="center"
          >
            <Paper
              elevation={0}
              sx={{
                p: "2px 14px",
                display: "flex",
                alignItems: "center",
                width: "100%",
                maxWidth: 600,
                border: `1.5px solid ${theme.palette.divider}`,
                borderRadius: 3,
                background: theme.palette.background.paper,
                boxShadow: isLight
                  ? "0 2px 8px 0 rgba(2,89,165,0.05)"
                  : "0 3px 12px 0 rgba(0,188,212,0.08)",
                transition: "border-color 0.18s, box-shadow 0.18s",
                "&:hover, &:focus-within": {
                  borderColor: theme.palette.primary.light,
                  boxShadow: isLight
                    ? "0 4px 16px 0 rgba(2,89,165,0.10)"
                    : "0 6px 20px 0 rgba(0,188,212,0.14)",
                },
              }}
            >
              <IconButton
                sx={{ p: "10px", color: theme.palette.text.secondary }}
                aria-label="menu"
              >
                <MenuIcon />
              </IconButton>
              <InputBase
                sx={{
                  ml: 1,
                  flex: 1,
                  fontSize: "1.05em",
                  color: theme.palette.text.primary,
                }}
                placeholder={t("topics.searchTopics")}
                inputProps={{ "aria-label": "search on topics" }}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
              />
              <IconButton
                sx={{ p: "10px", color: theme.palette.primary.main }}
                aria-label="search"
                onClick={(e) => {
                  e.preventDefault();
                  handleSearch();
                }}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={20} color="primary" />
                ) : (
                  <SearchIcon />
                )}
              </IconButton>
            </Paper>
          </Grid>

          {/* View Toggle Buttons */}
          <Grid
            item
            size={{ xs: 12, md: 3 }}
            display="flex"
            justifyContent={{ xs: "center", md: "flex-end" }}
          >
            <Box display="flex">
              <CustomHeaderButton
                active={filterView}
                onClick={() => setFilterView(!filterView)}
                icon={<FilterAltOutlinedIcon />}
              />
              <CustomHeaderButton
                active={view === "grid"}
                onClick={() => setView("grid")}
                icon={<GridViewIcon />}
                tooltip={t("topics.gridView")}
              />
              <CustomHeaderButton
                active={view === "list"}
                onClick={() => setView("list")}
                icon={<ListIcon />}
                tooltip={t("topics.listView")}
              />
            </Box>
          </Grid>
          {filterView && (
            <Grid className="w-full" container spacing={2}>
              <Grid item size={{ xs: 12 }}>
                <Divider />
              </Grid>
              <Grid item size={{ xs: 12 }}>
                <Typography>{t("topics.filterByCategory")}</Typography>
              </Grid>

              <Grid item size={{ xs: 12 }}>
                <ValidatorForm onSubmit={() => { }} process={false}>
                  <Grid container spacing={1}>
                    <Grid item size={{ xs: 12, md: 3 }}>
                      <Autocomplete
                        name={"category"}
                        label={t("common.category")}
                        value={
                          categoryData.find(
                            (item) => item.id === filters.category
                          ) || null
                        }
                        onChange={handleCategoryChange}
                        options={categoryData}
                        getOptionLabel={(option) => option.category_name || ""}
                        placeholder={t("category.selectCategory")}
                      />
                    </Grid>
                    <Grid item size={{ xs: 12, md: 3 }}>
                      <DatePicker
                        name="from"
                        label={t("topics.fromDate")}
                        value={filters.fromDate}
                        onChange={(date) =>
                          handleDateFilterChange(date, filters.toDate)
                        }
                      />
                    </Grid>
                    <Grid item size={{ xs: 12, md: 3 }}>
                      <DatePicker
                        name="to"
                        label={t("topics.toDate")}
                        value={filters.toDate}
                        onChange={(date) =>
                          handleDateFilterChange(filters.fromDate, date)
                        }
                      />
                    </Grid>
                    <Grid item size={{ xs: 12, md: 3 }}>
                      <Autocomplete
                        name={"has_contributions"}
                        label={t("topics.contributionStatus")}
                        value={
                          filters.has_contributions !== null
                            ? filters.has_contributions === true
                              ? { label: t("topics.contributed"), value: true }
                              : {
                                label: t("topics.notContributed"),
                                value: false,
                              }
                            : null
                        }
                        onChange={(e, newValue) => {
                          dispatch(
                            setFilters({
                              has_contributions: newValue
                                ? newValue.value
                                : null,
                            })
                          );
                          dispatch(fetchTopics());
                        }}
                        options={[
                          { label: t("topics.contributed"), value: true },
                          { label: t("topics.notContributed"), value: false },
                        ]}
                        getOptionLabel={(option) => option.label || ""}
                        placeholder={t("common.status")}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={1} sx={{ mt: 1 }}>
                    <Grid item size={{ xs: 12 }}>
                      <Box
                        display="flex"
                        gap={1}
                        alignItems="flex-end"
                        height="100%"
                        justifyContent={"flex-end"}
                      >
                        <Button
                          startIcon={<FilterAltOffIcon />}
                          size="large"
                          onClick={handleClearFilters}
                          variant="outlined"
                        >
                          {t("topics.clearFilters")}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </ValidatorForm>
              </Grid>
              {/* 
                            {chipData.map((data) => (
                                <Chip
                                    key={data.key}
                                    onClick={() => { }}
                                    label={data.label}
                                // onDelete={data.label === 'React' ? undefined : handleDelete(data)}
                                />
                            )
                            )} */}
            </Grid>
          )}
        </Grid>
      </Box>

      <Box mt={2}>{view === "grid" ? <GridView /> : <ListView />}</Box>
    </>
  );
};

export default AllTopics;
